import { useCallback, useSyncExternalStore } from 'react'

/**
 * A handful of tabs doesn't need a router library. This is the History API plus
 * a subscription, so deep links and the Android back button both work.
 */
export const ROUTES = [
  'now',
  'today',
  'week',
  'track',
  'iterations',
  'insights',
  'settings',
] as const
export type Route = (typeof ROUTES)[number]

const PATHS: Record<Route, string> = {
  now: '/',
  today: '/today',
  week: '/week',
  track: '/track',
  iterations: '/iterations',
  insights: '/insights',
  settings: '/settings',
}

const NAVIGATION_EVENT = 'timegrid:navigate'

export function routeFromPath(pathname: string): Route {
  const found = ROUTES.find((route) => PATHS[route] === pathname)
  return found ?? 'now'
}

export function pathForRoute(route: Route): string {
  return PATHS[route]
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange)
  window.addEventListener(NAVIGATION_EVENT, onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener(NAVIGATION_EVENT, onChange)
  }
}

export function useRoute(): Route {
  return useSyncExternalStore(
    subscribe,
    () => routeFromPath(window.location.pathname),
    () => 'now' as Route,
  )
}

export function navigate(route: Route, { replace = false } = {}): void {
  const path = PATHS[route]
  if (window.location.pathname === path) return
  if (replace) window.history.replaceState(null, '', path)
  else window.history.pushState(null, '', path)
  window.dispatchEvent(new Event(NAVIGATION_EVENT))
}

export function useNavigate(): (route: Route) => void {
  return useCallback((route: Route) => navigate(route), [])
}
